import React, { PropsWithChildren, useMemo } from 'react';
import { Table, TableProps } from 'antd';
import { ColumnType } from 'antd/lib/table';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import './DataTable.less';

export type DataTableColumn<T> = ColumnType<T> & {
  display?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
};
export type DataTableColumns<T> = DataTableColumn<T>[];
export type DataTablePagination = { page: number; size: number };
type Props<T> = {
  columns: DataTableColumns<T>;
  mobileRow?: React.ComponentType<{ value: any; record: T; index: number }>;
  onClick?: (item: T, index: number) => void;
  total: number;
  onPaginationChange?: (pagination: DataTablePagination) => void;
  dtPagination?: DataTablePagination;
} & Omit<TableProps<T>, 'columns'>;

// eslint-disable-next-line @typescript-eslint/ban-types,@typescript-eslint/explicit-module-boundary-types,@typescript-eslint/no-unused-vars
export const DataTable = <T extends object, ID = number>(props: PropsWithChildren<Props<T>>) => {
  const { columns, mobileRow, dtPagination } = props;
  // const [pageSize, setPageSize] = useState<number>(10);
  // const [current, setCurrent] = useState<number>(1);
  const bp = useBreakpoint();
  const cols = useMemo(() => {
    if (!bp.md && mobileRow) {
      const col = { ...columns[0] };
      col.display = 'md';
      const MobRow = mobileRow;
      // eslint-disable-next-line react/display-name
      col.render = (value: any, record: T, index: number) => (
        <MobRow value={value} record={record} index={index} />
      );
      return [col];
    }
    return columns.filter((c) => !c.display || bp[c.display]);
  }, [bp, columns, mobileRow]);
  return (
    <Table<T>
      {...props}
      scroll={props.scroll ?? { x: 'scroll' }}
      className={`data-table ${props.className ?? ''}`}
      columns={cols}
      showHeader={
        props.showHeader !== undefined ? props.showHeader : !(!bp.md && mobileRow !== undefined)
      }
      onRow={
        props.onRow ??
        ((record, index: number | undefined) => ({
          onClick: () => props.onClick?.(record, index!),
        }))
      }
      pagination={
        props.pagination ?? {
          total: props.total,
          pageSize: dtPagination?.size ?? 10,
          current: dtPagination?.page ?? 1,
          showTotal: (total, range) => {
            return `Displaying ${range[0]} to ${range[1]} of ${total} items`;
          },
          onChange: (page1, pageSize1) => {
            props.onPaginationChange?.({ page: page1, size: pageSize1 ?? 10 });
          },
          onShowSizeChange: (current, size) => {
            props.onPaginationChange?.({ page: 1, size: size ?? 10 });
          },
          showSizeChanger: true,
          position: ['bottomRight'],
          simple: !bp.md,
        }
      }
    />
  );
};
